import { useEffect, useState, createContext, useContext, useMemo } from "react";
import Cookies from "universal-cookie";
import { sendGTMEvent } from "@next/third-parties/google";
import { Box, Flex, Grid } from "@chakra-ui/react";
import Button from "components/base/Button";
import Trans from "components/base/Trans";
import Link from "components/base/Link";

const CookieConsentsContext = createContext(null);

const CookieConsentsProvider = ({ children, cookieName, expiryInDays }) => {
  const [cookies] = useState(() => new Cookies());
  const [consents, setConsents] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const storedConsents = cookies.get(cookieName);
      if (storedConsents && Array.isArray(storedConsents)) {
        setConsents(storedConsents);
      }
      setIsLoaded(true);
    }, 0);
    return () => clearTimeout(timeout);
  }, [cookies, cookieName]);

  const value = useMemo(() => {
    return {
      get: () => consents,
      add: (consent) => {
        const newConsents = [...new Set([...consents, consent])];
        setConsents(newConsents);

        const expires = new Date();
        expires.setDate(expires.getDate() + expiryInDays);

        cookies.set(cookieName, newConsents, { path: "/", expires });
      },
      isLoaded,
    };
  }, [consents, cookieName, expiryInDays, cookies, isLoaded]);

  return (
    <CookieConsentsContext.Provider value={value}>
      {children}
    </CookieConsentsContext.Provider>
  );
};

const useCookieConsents = () => {
  const context = useContext(CookieConsentsContext);
  if (!context) {
    throw new Error(
      "useCookieConsents must be used within a CookieConsentsProvider",
    );
  }
  return context;
};

const CookieBanner = () => {
  const [show, setShow] = useState(false);
  const [sendGtagConsent, setSendGtagConsent] = useState(false);

  const cookieConsents = useCookieConsents();

  useEffect(() => {
    if (!cookieConsents.isLoaded) return;

    const timeout = setTimeout(() => {
      if (cookieConsents.get().length === 0) {
        setShow(true);
      } else if (!sendGtagConsent) {
        sendGTMEvent({
          event: "consent_update",
          ad_user_data: "granted",
          ad_personalization: "granted",
          ad_storage: "granted",
          analytics_storage: "granted",
        });
        setSendGtagConsent(true);
      }
    }, 3000);
    return () => clearTimeout(timeout);
  }, [cookieConsents, sendGtagConsent]);

  if (!show) {
    return null;
  }

  const acceptCookies = () => {
    cookieConsents.add("analytics");
    setShow(false);
    if (!sendGtagConsent) {
      sendGTMEvent({
        event: "consent_update",
        ad_user_data: "granted",
        ad_personalization: "granted",
        ad_storage: "granted",
        analytics_storage: "granted",
      });
      setSendGtagConsent(true);
    }
  };

  return (
    <Flex
      zIndex={9999999999}
      position="fixed"
      bottom={0}
      bg="#000000cc"
      w="full"
      justifyContent="center"
    >
      <Grid
        templateColumns={["1fr", null, "auto auto", null, null]}
        py={[2, 2, 2, "32px"]}
        mx={[4, 4, 4, 20, 20]}
        gap={1}
        maxW={["100%", null, null, "xl", "xl", "xl"]}
      >
        <Box
          color="white"
          fontSize={["xs", null, "sm", "sm", null]}
          pr={[2, null, 8, null, "10%"]}
          pt={1}
        >
          <Trans id="LBL_PRIVACY_POLICY_COOKIES_DESCRIPTION" />

          <Link href="/privacy-policy" fontWeight="bold" ml={2} target="_blank">
            <Trans id="LBL_PRIVACY_POLICY_COOKIES_CTA" />
          </Link>
        </Box>
        <Flex
          align="center"
          justify="center"
          py={[2, null, 0]}
          px={[4, null, 2]}
        >
          <Button
            zIndex={10}
            width={["100%", null, "auto"]}
            maxW="620px"
            px={12}
            trackLabel="privacy_policy"
            variant="outline"
            onClick={acceptCookies}
          >
            <Trans id="LBL_PRIVACY_POLICY_COOKIES_ACCEPT" />
          </Button>
        </Flex>
      </Grid>
    </Flex>
  );
};

const PrivacyPolicyConsent = () => (
  <CookieConsentsProvider cookieName="cookieConsents" expiryInDays={365}>
    <CookieBanner />
  </CookieConsentsProvider>
);

export default PrivacyPolicyConsent;
